import { useState, useEffect, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { settingsAPI } from "../../services/api";
import { useTheme, DARK, LIGHT } from "../../components/Layout";

interface Location {
  id: string;
  name: string;
  address: string;
  phone: string;
}

interface Settings {
  company_name: string;
  company_slogan: string;
  company_logo: string;
  company_phone: string;
  company_email: string;
  company_website: string;
  company_address: string;
  company_currency: string;
  fiscal_year_start: string;
  fiscal_year_end: string;
  paper_size: string;
  print_logo: string;
  print_header: string;
  print_footer: string;
  login_timeout: string;
  default_theme: string;
  calendar_type: string;
  multi_location: string;
  locations: Location[];
  backup_schedule: string;
}

const defaultSettings: Settings = {
  company_name: "",
  company_slogan: "",
  company_logo: "",
  company_phone: "",
  company_email: "",
  company_website: "",
  company_address: "",
  company_currency: "AFN",
  fiscal_year_start: "01-01",
  fiscal_year_end: "12-31",
  paper_size: "A4",
  print_logo: "true",
  print_header: "",
  print_footer: "",
  login_timeout: "480",
  default_theme: "dark",
  calendar_type: "both",
  multi_location: "false",
  locations: [],
  backup_schedule: "weekly",
};

const currencies = [
  { code: "AFN", name: "افغانی افغانستان", symbol: "؋" },
  { code: "IRR", name: "ریال ایران", symbol: "﷼" },
  { code: "USD", name: "دلار آمریکا", symbol: "$" },
  { code: "EUR", name: "یورو", symbol: "€" },
  { code: "GBP", name: "پوند انگلیس", symbol: "£" },
  { code: "AED", name: "درهم امارات", symbol: "د.إ" },
  { code: "PKR", name: "روپیه پاکستان", symbol: "₨" },
  { code: "TRY", name: "لیره ترکیه", symbol: "₺" },
  { code: "CNY", name: "یوان چین", symbol: "¥" },
];

export default function SystemSettings() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const { company, setCompany, currentUser } = useApp();

  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [activeTab, setActiveTab] = useState("company");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [newLocation, setNewLocation] = useState({ name: "", address: "", phone: "" });
  const [showAddLocation, setShowAddLocation] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);

  // 🆕 state های ربات تلگرام
  const [tgSettings, setTgSettings] = useState<any>({
    bot_token: "",
    bot_username: "",
    is_active: 0,
    webhook_url: "",
    daily_report_time: "20:00",
  });
  const [tgUsers, setTgUsers] = useState<any[]>([]);
  const [tgLoaded, setTgLoaded] = useState(false);
  const [tgSaving, setTgSaving] = useState(false);
  const [showAddTgUser, setShowAddTgUser] = useState(false);
  const [editingTgUser, setEditingTgUser] = useState<any>(null);
  const [newTgUser, setNewTgUser] = useState<any>({
    telegram_chat_id: "",
    full_name: "",
    phone: "",
    notify_sales: true,
    notify_purchases: true,
    notify_cogs: false,
    notify_payment_in: true,
    notify_payment_out: true,
    notify_expense: true,
    notify_transfers: false,
    can_query_customers: true,
    notes: "",
  });

  const isAdmin = currentUser?.role === "admin";

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await settingsAPI.get();
      if (res.success) {
        const s = res.settings;
        setSettings({
          company_name: s.company_name || "",
          company_slogan: s.company_slogan || "",
          company_logo: s.company_logo || "",
          company_phone: s.company_phone || "",
          company_email: s.company_email || "",
          company_website: s.company_website || "",
          company_address: s.company_address || "",
          company_currency: s.company_currency || "AFN",
          fiscal_year_start: s.fiscal_year_start || "01-01",
          fiscal_year_end: s.fiscal_year_end || "12-31",
          paper_size: s.paper_size || "A4",
          print_logo: s.print_logo || "true",
          print_header: s.print_header || "",
          print_footer: s.print_footer || "",
          login_timeout: s.login_timeout || "480",
          default_theme: s.default_theme || "dark",
          calendar_type: s.calendar_type || "both",
          multi_location: s.multi_location === true || s.multi_location === "true" ? "true" : "false",          locations: Array.isArray(s.locations) ? s.locations : [],
          backup_schedule: s.backup_schedule || "weekly",
        });
      }
    } catch {}
    setLoading(false);
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    setMsg(null);
    try {
      const res = await settingsAPI.update(settings);
      if (res.success) {
        setCompany({
          name: settings.company_name || company.name,
          logo: settings.company_logo || null,
          slogan: settings.company_slogan || company.slogan,
        });
        setMsg({ type: "success", text: "تنظیمات با موفقیت ذخیره شد ✓" });
      } else {
        setMsg({ type: "error", text: res.message || "خطا در ذخیره" });
      }
    } catch {
      setMsg({ type: "error", text: "خطا در اتصال به سرور" });
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 4000);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMsg({ type: "error", text: "حجم لوگو نباید بیشتر از ۲ مگابایت باشد" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setSettings(s => ({ ...s, company_logo: ev.target?.result as string }));
    reader.readAsDataURL(file);
  };

  const addLocation = () => {
    if (!newLocation.name.trim()) return;
    const loc: Location = { id: Date.now().toString(), ...newLocation };
    setSettings(s => ({ ...s, locations: [...s.locations, loc] }));
    setNewLocation({ name: "", address: "", phone: "" });
    setShowAddLocation(false);
  };

  const removeLocation = (id: string) =>
    setSettings(s => ({ ...s, locations: s.locations.filter(l => l.id !== id) }));

  const set = (key: keyof Settings, val: string) =>
    setSettings(s => ({ ...s, [key]: val }));

  // ═══════════════════════════════════════════════════
  // 🤖 توابع ربات تلگرام
  // ═══════════════════════════════════════════════════
  const TG_API = "https://www.hamidigroupfinance.com/api/telegram.php";

  const tgFetch = async (action: string, data: any = {}) => {
    const token = localStorage.getItem("auth_token") || "";
    try {
      const r = await fetch(TG_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ action, ...data }),
      });
      const text = await r.text();
      if (!text) {
        return { success: false, message: "پاسخ خالی از سرور (احتمالاً timeout)" };
      }
      try {
        return JSON.parse(text);
      } catch {
        // اگر JSON نبود، متن خام برگردون
        return { 
          success: false, 
          message: "خطای سرور: " + text.substring(0, 500),
        };
      }
    } catch (e: any) {
      return { success: false, message: e?.message || "خطا در ارتباط" };
    }
  };

  const loadTelegram = async () => {
    const [r1, r2] = await Promise.all([
      tgFetch("get_settings"),
      tgFetch("list_users"),
    ]);
    if (r1.success) setTgSettings(r1.settings);
    if (r2.success) setTgUsers(r2.users);
    setTgLoaded(true);
  };

  useEffect(() => {
    if (activeTab === "telegram" && !tgLoaded) loadTelegram();
  }, [activeTab]);

  const saveTgSettings = async () => {
    setTgSaving(true);
    const r = await tgFetch("save_settings", tgSettings);
    setTgSaving(false);
    if (r.success) {
      setMsg({ type: "success", text: "تنظیمات ربات ذخیره شد" + (r.bot_username ? ` (@${r.bot_username})` : "") });
      loadTelegram();
    } else {
      setMsg({ type: "error", text: r.message || "خطا" });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const setupWebhook = async () => {
    const url = TG_API + "?webhook=1";
    const r = await tgFetch("set_webhook", { webhook_url: url });
    if (r.success) {
      setMsg({ type: "success", text: "Webhook با موفقیت تنظیم شد" });
      loadTelegram();
    } else {
      setMsg({ type: "error", text: r.message || "خطا در تنظیم Webhook" });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const addTgUser = async () => {
    if (!newTgUser.telegram_chat_id || !newTgUser.full_name) {
      setMsg({ type: "error", text: "شناسه چت و نام کامل الزامی است" });
      setTimeout(() => setMsg(null), 4000);
      return;
    }
    const payload: any = { ...newTgUser };
    Object.keys(payload).forEach(k => {
      if (typeof payload[k] === "boolean") payload[k] = payload[k] ? 1 : 0;
    });
    const r = await tgFetch("add_user", payload);
    if (r.success) {
      setMsg({ type: "success", text: "کاربر اضافه شد" });
      setShowAddTgUser(false);
      setNewTgUser({
        telegram_chat_id: "", full_name: "", phone: "",
        notify_sales: true, notify_purchases: true, notify_cogs: false,
        notify_payment_in: true, notify_payment_out: true, notify_expense: true, notify_transfers: false,
        can_query_customers: true, notes: "",
      });
      loadTelegram();
    } else {
      setMsg({ type: "error", text: r.message || "خطا" });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const updateTgUser = async (u: any) => {
    const payload: any = { ...u };
    Object.keys(payload).forEach(k => {
      if (typeof payload[k] === "boolean") payload[k] = payload[k] ? 1 : 0;
    });
    const r = await tgFetch("update_user", payload);
    if (r.success) {
      setMsg({ type: "success", text: "تغییرات ذخیره شد" });
      setEditingTgUser(null);
      loadTelegram();
    } else {
      setMsg({ type: "error", text: r.message || "خطا" });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const deleteTgUser = async (id: number, name: string) => {
    if (!window.confirm(`حذف کاربر "${name}" تأیید می‌کنید؟`)) return;
    const r = await tgFetch("delete_user", { id });
    if (r.success) {
      setMsg({ type: "success", text: "کاربر حذف شد" });
      loadTelegram();
    } else {
      setMsg({ type: "error", text: r.message || "خطا" });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const testTgSend = async (chatId: string) => {
    const r = await tgFetch("test_send", { chat_id: chatId });
    if (r.success) {
      setMsg({ type: "success", text: "پیام تست ارسال شد ✅" });
    } else {
      setMsg({ type: "error", text: r.message || "خطا در ارسال" });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const runDailyReportNow = async () => {
    if (!window.confirm("گزارش روزانه را همین الان به تمام کاربران بفرستم؟")) return;
    setMsg({ type: "success", text: "در حال اجرا... منتظر بمانید" });
    const r = await tgFetch("run_daily_report");
    if (r.success) {
      const res = r.result || {};
      setMsg({ type: "success", text: `گزارش ارسال شد — موفق: ${res.sent || 0}, ناموفق: ${res.failed || 0}` });
    } else {
      setMsg({ type: "error", text: r.message || "خطا" });
    }
    setTimeout(() => setMsg(null), 6000);
  };

  const tabs = [
    { id: "company", label: "مشخصات شرکت", icon: "🏢" },
    { id: "locations", label: "شعبه‌ها", icon: "📍" },
    { id: "financial", label: "تنظیمات مالی", icon: "💰" },
    { id: "print", label: "تنظیمات چاپ", icon: "🖨️" },
    { id: "system", label: "تنظیمات سیستم", icon: "⚙️" },
    { id: "backup", label: "بکاپ", icon: "💾" },
    { id: "telegram", label: "ربات تلگرام", icon: "🤖" },
  ];

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 14px", borderRadius: 10,
    border: `1.5px solid ${T.border}`, background: T.input,
    color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    outline: "none", boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: T.subtext,
    display: "block", marginBottom: 6,
  };

  const cardStyle: React.CSSProperties = {
    background: T.card, border: `1px solid ${T.cardBorder}`,
    borderRadius: 16, padding: 24, marginBottom: 20,
  };

  const sectionTitle = (title: string, icon: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.text }}>{title}</h3>
    </div>
  );

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
      <div style={{ color: T.subtext, fontSize: 14, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>در حال بارگذاری...</div>
    </div>
  );

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif", padding: "0 0 40px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.text }}>⚙️ تنظیمات سیستم</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: T.subtext }}>پیکربندی کامل نرم‌افزار</p>
        </div>
        {isAdmin && (
          <button onClick={handleSave} disabled={saving} style={{
            padding: "10px 28px",
            background: saving ? "rgba(59,130,246,0.5)" : "linear-gradient(135deg,#3b82f6,#8b5cf6)",
            border: "none", borderRadius: 12, cursor: saving ? "not-allowed" : "pointer",
            color: "white", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            boxShadow: saving ? "none" : "0 4px 16px rgba(59,130,246,0.4)",
          }}>
            {saving ? "در حال ذخیره..." : "💾 ذخیره تنظیمات"}
          </button>
        )}
      </div>

      {/* Message */}
      {msg && (
        <div style={{
          padding: "12px 16px", borderRadius: 12, marginBottom: 20,
          background: msg.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
          border: `1px solid ${msg.type === "success" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
          color: msg.type === "success" ? "#22c55e" : "#ef4444",
          fontSize: 13, fontWeight: 600,
        }}>
          {msg.text}
        </div>
      )}

      {!isAdmin && (
        <div style={{
          padding: "12px 16px", borderRadius: 12, marginBottom: 20,
          background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)",
          color: "#f59e0b", fontSize: 13,
        }}>
          🔒 فقط مدیر سیستم می‌تواند تنظیمات را تغییر دهد
        </div>
      )}

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

        {/* Sidebar Tabs */}
        <div style={{ width: 200, flexShrink: 0 }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              width: "100%", padding: "12px 16px",
              background: activeTab === tab.id
                ? "linear-gradient(135deg,rgba(59,130,246,0.2),rgba(139,92,246,0.2))"
                : "transparent",
              border: activeTab === tab.id ? "1px solid rgba(59,130,246,0.3)" : "1px solid transparent",
              borderRadius: 12, cursor: "pointer",
              color: activeTab === tab.id ? "#3b82f6" : T.subtext,
              fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 400,
              fontFamily: "inherit", textAlign: "right",
              display: "flex", alignItems: "center", gap: 8,
              marginBottom: 6, transition: "all 0.2s",
            }}>
              <span>{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>

          {/* مشخصات شرکت */}
          {activeTab === "company" && (
            <div>
              <div style={cardStyle}>
                {sectionTitle("لوگوی شرکت", "🖼️")}
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <div style={{
                    width: 100, height: 100, borderRadius: 20,
                    background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden", flexShrink: 0, border: `2px solid ${T.border}`,
                  }}>
                    {settings.company_logo
                      ? <img src={settings.company_logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontSize: 36, color: "white", fontWeight: 900 }}>
                          {settings.company_name ? settings.company_name.charAt(0) : "؟"}
                        </span>
                    }
                  </div>
                  <div>
                    <button onClick={() => logoRef.current?.click()} disabled={!isAdmin} style={{
                      padding: "9px 20px", background: T.input,
                      border: `1.5px solid ${T.border}`, borderRadius: 10,
                      cursor: isAdmin ? "pointer" : "not-allowed",
                      color: T.text, fontSize: 13, fontFamily: "inherit",
                      marginBottom: 8, display: "block",
                    }}>📁 انتخاب لوگو</button>
                    {settings.company_logo && (
                      <button onClick={() => set("company_logo", "")} disabled={!isAdmin} style={{
                        padding: "9px 20px", background: "rgba(239,68,68,0.1)",
                        border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10,
                        cursor: isAdmin ? "pointer" : "not-allowed",
                        color: "#ef4444", fontSize: 13, fontFamily: "inherit",
                      }}>🗑️ حذف لوگو</button>
                    )}
                    <p style={{ margin: "8px 0 0", fontSize: 11, color: T.subtext }}>
                      فرمت: JPG، PNG — حداکثر ۲ مگابایت
                    </p>
                    <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
                  </div>
                </div>
              </div>

              <div style={cardStyle}>
                {sectionTitle("اطلاعات شرکت", "🏢")}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>نام شرکت *</label>
                    <input value={settings.company_name} onChange={e => set("company_name", e.target.value)}
                      disabled={!isAdmin} placeholder="نام شرکت را وارد کنید" style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>شعار شرکت</label>
                    <input value={settings.company_slogan} onChange={e => set("company_slogan", e.target.value)}
                      disabled={!isAdmin} placeholder="شعار یا توضیح کوتاه شرکت" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>شماره تماس</label>
                    <input value={settings.company_phone} onChange={e => set("company_phone", e.target.value)}
                      disabled={!isAdmin} placeholder="+93 700 000 000" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>ایمیل</label>
                    <input value={settings.company_email} onChange={e => set("company_email", e.target.value)}
                      disabled={!isAdmin} placeholder="info@company.com" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>وبسایت</label>
                    <input value={settings.company_website} onChange={e => set("company_website", e.target.value)}
                      disabled={!isAdmin} placeholder="www.company.com" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>آدرس</label>
                    <input value={settings.company_address} onChange={e => set("company_address", e.target.value)}
                      disabled={!isAdmin} placeholder="آدرس شرکت" style={inputStyle} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* شعبه‌ها */}
          {activeTab === "locations" && (
            <div style={cardStyle}>
              {sectionTitle("مدیریت چند شعبه", "📍")}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 13, color: T.subtext }}>فعال‌سازی چند شعبه:</span>
                  <div onClick={() => isAdmin && set("multi_location", settings.multi_location === "true" ? "false" : "true")}
                    style={{
                      width: 48, height: 26, borderRadius: 13,
                      background: settings.multi_location === "true" ? "#3b82f6" : T.border,
                      cursor: isAdmin ? "pointer" : "not-allowed",
                      position: "relative", transition: "all 0.3s",
                    }}>
                    <div style={{
                      position: "absolute", top: 3,
                      right: settings.multi_location === "true" ? 3 : 22,
                      width: 20, height: 20, borderRadius: "50%",
                      background: "white", transition: "all 0.3s",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                    }} />
                  </div>
                  <span style={{ fontSize: 12, color: settings.multi_location === "true" ? "#3b82f6" : T.subtext, fontWeight: 600 }}>
                    {settings.multi_location === "true" ? "فعال" : "غیرفعال"}
                  </span>
                </div>
                {isAdmin && settings.multi_location === "true" && (
                  <button onClick={() => setShowAddLocation(!showAddLocation)} style={{
                    padding: "8px 16px", background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                    border: "none", borderRadius: 10, cursor: "pointer",
                    color: "white", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                  }}>+ افزودن شعبه</button>
                )}
              </div>

              {settings.multi_location === "true" && (
                <>
                  {showAddLocation && (
                    <div style={{
                      padding: 16, borderRadius: 12, marginBottom: 16,
                      background: dark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.05)",
                      border: "1px solid rgba(59,130,246,0.2)",
                    }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={labelStyle}>نام شعبه *</label>
                          <input value={newLocation.name} onChange={e => setNewLocation(s => ({ ...s, name: e.target.value }))}
                            placeholder="مثلاً: شعبه مرکزی" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>آدرس</label>
                          <input value={newLocation.address} onChange={e => setNewLocation(s => ({ ...s, address: e.target.value }))}
                            placeholder="آدرس شعبه" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>تلفن</label>
                          <input value={newLocation.phone} onChange={e => setNewLocation(s => ({ ...s, phone: e.target.value }))}
                            placeholder="شماره تماس" style={inputStyle} />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={addLocation} style={{
                          padding: "8px 20px", background: "#22c55e",
                          border: "none", borderRadius: 8, cursor: "pointer",
                          color: "white", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                        }}>✓ افزودن</button>
                        <button onClick={() => setShowAddLocation(false)} style={{
                          padding: "8px 20px", background: T.input,
                          border: `1px solid ${T.border}`, borderRadius: 8,
                          cursor: "pointer", color: T.subtext, fontSize: 12, fontFamily: "inherit",
                        }}>انصراف</button>
                      </div>
                    </div>
                  )}

                  {settings.locations.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px", color: T.subtext, fontSize: 13 }}>
                      هنوز شعبه‌ای اضافه نشده
                    </div>
                  ) : (
                    settings.locations.map((loc, i) => (
                      <div key={loc.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "14px 16px", borderRadius: 12, marginBottom: 8,
                        background: T.input, border: `1px solid ${T.border}`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "white", fontSize: 14, fontWeight: 700, flexShrink: 0,
                          }}>{i + 1}</div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{loc.name}</div>
                            <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>
                              {loc.address && `📍 ${loc.address}`}
                              {loc.address && loc.phone && " · "}
                              {loc.phone && `📞 ${loc.phone}`}
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <button onClick={() => removeLocation(loc.id)} style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)",
                            cursor: "pointer", color: "#ef4444", fontSize: 18,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>×</button>
                        )}
                      </div>
                    ))
                  )}
                </>
              )}

              {settings.multi_location !== "true" && (
                <div style={{ textAlign: "center", padding: "24px", color: T.subtext, fontSize: 13 }}>
                  برای مدیریت چند شعبه، ابتدا این قابلیت را فعال کنید
                </div>
              )}
            </div>
          )}

          {/* تنظیمات مالی */}
          {activeTab === "financial" && (
            <div>
              <div style={cardStyle}>
                {sectionTitle("ارز سیستم", "💰")}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>ارز پیش‌فرض سیستم</label>
                    <select value={settings.company_currency} onChange={e => set("company_currency", e.target.value)}
                      disabled={!isAdmin} style={inputStyle}>
                      {currencies.map(c => (
                        <option key={c.code} value={c.code}>{c.symbol} — {c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div style={cardStyle}>
                {sectionTitle("سال مالی", "📅")}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={labelStyle}>شروع سال مالی (ماه-روز)</label>
                    <input value={settings.fiscal_year_start} onChange={e => set("fiscal_year_start", e.target.value)}
                      disabled={!isAdmin} placeholder="01-01" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>پایان سال مالی (ماه-روز)</label>
                    <input value={settings.fiscal_year_end} onChange={e => set("fiscal_year_end", e.target.value)}
                      disabled={!isAdmin} placeholder="12-31" style={inputStyle} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* تنظیمات چاپ */}
          {activeTab === "print" && (
            <div>
              <div style={cardStyle}>
                {sectionTitle("تنظیمات چاپ", "🖨️")}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <label style={labelStyle}>اندازه کاغذ</label>
                    <select value={settings.paper_size} onChange={e => set("paper_size", e.target.value)}
                      disabled={!isAdmin} style={inputStyle}>
                      <option value="A4">A4</option>
                      <option value="A5">A5</option>
                      <option value="Letter">Letter</option>
                      <option value="Receipt">رسید (۸۰mm)</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>نمایش لوگو در چاپ</label>
                    <select value={settings.print_logo} onChange={e => set("print_logo", e.target.value)}
                      disabled={!isAdmin} style={inputStyle}>
                      <option value="true">بله</option>
                      <option value="false">خیر</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>هدر چاپ</label>
                    <input value={settings.print_header} onChange={e => set("print_header", e.target.value)}
                      disabled={!isAdmin} placeholder="متن هدر برای همه اسناد چاپی" style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>فوتر چاپ</label>
                    <input value={settings.print_footer} onChange={e => set("print_footer", e.target.value)}
                      disabled={!isAdmin} placeholder="متن فوتر — مثلاً: با تشکر از خرید شما" style={inputStyle} />
                  </div>
                </div>
              </div>

              <div style={cardStyle}>
                {sectionTitle("پیش‌نمایش هدر اسناد", "👁️")}
                <div style={{
                  padding: 24, borderRadius: 12,
                  background: "white", color: "#1e293b",
                  border: "1px solid #e2e8f0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
                    {settings.print_logo === "true" && settings.company_logo && (
                      <img src={settings.company_logo} alt="logo" style={{ width: 60, height: 60, objectFit: "contain" }} />
                    )}
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{settings.company_name || "نام شرکت"}</div>
                      {settings.company_slogan && <div style={{ fontSize: 12, color: "#64748b" }}>{settings.company_slogan}</div>}
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                        {settings.company_phone && `📞 ${settings.company_phone}  `}
                        {settings.company_email && `✉️ ${settings.company_email}`}
                      </div>
                    </div>
                  </div>
                  {settings.print_header && (
                    <div style={{ fontSize: 12, color: "#475569", borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                      {settings.print_header}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* تنظیمات سیستم */}
          {activeTab === "system" && (
            <div style={cardStyle}>
              {sectionTitle("تنظیمات عمومی سیستم", "⚙️")}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={labelStyle}>تقویم پیش‌فرض</label>
                  <select value={settings.calendar_type} onChange={e => set("calendar_type", e.target.value)}
                    disabled={!isAdmin} style={inputStyle}>
                    <option value="both">شمسی و میلادی</option>
                    <option value="jalali">فقط شمسی</option>
                    <option value="gregorian">فقط میلادی</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>تم پیش‌فرض</label>
                  <select value={settings.default_theme} onChange={e => set("default_theme", e.target.value)}
                    disabled={!isAdmin} style={inputStyle}>
                    <option value="dark">تاریک</option>
                    <option value="light">روشن</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>مدت زمان خروج خودکار</label>
                  <select value={settings.login_timeout} onChange={e => set("login_timeout", e.target.value)}
                    disabled={!isAdmin} style={inputStyle}>
                    <option value="60">۱ ساعت</option>
                    <option value="240">۴ ساعت</option>
                    <option value="480">۸ ساعت</option>
                    <option value="1440">۲۴ ساعت</option>
                    <option value="0">هرگز</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* بکاپ */}
          {activeTab === "backup" && (
            <div style={cardStyle}>
              {sectionTitle("تنظیمات بکاپ", "💾")}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>زمان‌بندی بکاپ</label>
                <select value={settings.backup_schedule} onChange={e => set("backup_schedule", e.target.value)}
                  disabled={!isAdmin} style={inputStyle}>
                  <option value="daily">روزانه</option>
                  <option value="weekly">هفتگی</option>
                  <option value="monthly">ماهانه</option>
                  <option value="off">غیرفعال</option>
                </select>
              </div>
              <div style={{
                padding: "12px 16px", borderRadius: 10,
                background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)",
                fontSize: 12, color: "#f59e0b",
              }}>
                ⚠️ برای بکاپ خودکار از طریق سرور، از صفحه بکاپ استفاده کنید.
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════ */}
          {/* 🤖 تب ربات تلگرام                                    */}
          {/* ═══════════════════════════════════════════════════ */}
          {activeTab === "telegram" && (
            <>
              {/* ─── بخش 1: تنظیمات ربات ─── */}
              <div style={cardStyle}>
                {sectionTitle("تنظیمات ربات تلگرام", "🤖")}
                
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>🔑 توکن ربات (Bot Token)</label>
                  <input
                    type="text"
                    value={tgSettings.bot_token || ""}
                    onChange={e => setTgSettings({ ...tgSettings, bot_token: e.target.value })}
                    placeholder="123456789:ABCdefGHI..."
                    disabled={!isAdmin}
                    style={{ ...inputStyle, direction: "ltr" as const, fontFamily: "monospace" }}
                  />
                  <div style={{ fontSize: 11, color: T.subtext, marginTop: 6, lineHeight: 1.7 }}>
                    💡 برای دریافت توکن، به <strong>@BotFather</strong> در تلگرام پیام دهید و یک ربات جدید بسازید.
                  </div>
                </div>

                {tgSettings.bot_username && (
                  <div style={{
                    padding: "10px 14px", borderRadius: 8, marginBottom: 12,
                    background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)",
                    fontSize: 12, color: "#10b981", fontWeight: 700,
                  }}>
                    ✅ ربات شناسایی شد: <strong>@{tgSettings.bot_username}</strong>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>⏰ ساعت ارسال گزارش روزانه (به وقت کابل)</label>
                    <input
                      type="time"
                      value={tgSettings.daily_report_time || "20:00"}
                      onChange={e => setTgSettings({ ...tgSettings, daily_report_time: e.target.value })}
                      disabled={!isAdmin}
                      style={{ ...inputStyle, direction: "ltr" as const }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>وضعیت</label>
                    <select
                      value={tgSettings.is_active ? "1" : "0"}
                      onChange={e => setTgSettings({ ...tgSettings, is_active: parseInt(e.target.value) })}
                      disabled={!isAdmin}
                      style={inputStyle}
                    >
                      <option value="1">✅ فعال</option>
                      <option value="0">❌ غیرفعال</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={saveTgSettings}
                    disabled={tgSaving || !isAdmin}
                    style={{
                      padding: "10px 22px", borderRadius: 8, border: "none",
                      background: "linear-gradient(135deg,#3b82f6,#2563eb)",
                      color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    }}
                  >
                    {tgSaving ? "در حال ذخیره..." : "💾 ذخیره تنظیمات"}
                  </button>

                  {tgSettings.bot_token && (
                    <button
                      onClick={setupWebhook}
                      disabled={!isAdmin}
                      style={{
                        padding: "10px 22px", borderRadius: 8,
                        border: "1.5px solid #8b5cf6", background: "transparent",
                        color: "#8b5cf6", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                      }}
                    >
                      🔗 تنظیم Webhook
                    </button>
                  )}

                  <button
                    onClick={runDailyReportNow}
                    disabled={!isAdmin || !tgSettings.is_active}
                    style={{
                      padding: "10px 22px", borderRadius: 8,
                      border: "1.5px solid #10b981", background: "transparent",
                      color: "#10b981", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    }}
                  >
                    🚀 اجرای دستی گزارش روزانه
                  </button>
                </div>

                {tgSettings.webhook_url && (
                  <div style={{
                    marginTop: 12, padding: "8px 14px", borderRadius: 8,
                    background: dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.06)",
                    border: "1px solid rgba(139,92,246,0.2)",
                    fontSize: 11, color: T.subtext, direction: "ltr" as const,
                  }}>
                    🔗 Webhook: <code style={{ color: "#8b5cf6" }}>{tgSettings.webhook_url}</code>
                  </div>
                )}
              </div>

              {/* ─── بخش 2: لیست کاربران ─── */}
              <div style={{ ...cardStyle, marginTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  {sectionTitle(`کاربران ربات (${tgUsers.length})`, "👥")}
                  {isAdmin && (
                    <button
                      onClick={() => setShowAddTgUser(!showAddTgUser)}
                      style={{
                        padding: "8px 16px", borderRadius: 8, border: "none",
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                      }}
                    >
                      {showAddTgUser ? "✗ انصراف" : "+ کاربر جدید"}
                    </button>
                  )}
                </div>

                {/* فرم افزودن کاربر */}
                {showAddTgUser && (
                  <div style={{
                    padding: 16, borderRadius: 10, marginBottom: 16,
                    background: dark ? "rgba(16,185,129,0.06)" : "#f0fdf4",
                    border: "1.5px solid rgba(16,185,129,0.25)",
                  }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
                      <div>
                        <label style={labelStyle}>🆔 شناسه چت (Chat ID) *</label>
                        <input
                          type="text"
                          value={newTgUser.telegram_chat_id}
                          onChange={e => setNewTgUser({ ...newTgUser, telegram_chat_id: e.target.value })}
                          placeholder="123456789"
                          style={{ ...inputStyle, direction: "ltr" as const, fontFamily: "monospace" }}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>نام کامل *</label>
                        <input
                          type="text"
                          value={newTgUser.full_name}
                          onChange={e => setNewTgUser({ ...newTgUser, full_name: e.target.value })}
                          placeholder="نام و تخلص..."
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>📞 تلفن (اختیاری)</label>
                        <input
                          type="text"
                          value={newTgUser.phone}
                          onChange={e => setNewTgUser({ ...newTgUser, phone: e.target.value })}
                          placeholder="0700..."
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                      📊 گزارش‌های روزانه‌ای که این کاربر دریافت می‌کند:
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
                      {[
                        { key: "notify_sales", label: "🛒 فروشات" },
                        { key: "notify_purchases", label: "🛍️ خریدات" },
                        { key: "notify_cogs", label: "📦 COGS" },
                        { key: "notify_payment_in", label: "💰 دریافت‌ها" },
                        { key: "notify_payment_out", label: "💸 پرداخت‌ها" },
                        { key: "notify_expense", label: "🧾 مصارف" },
                        { key: "notify_transfers", label: "🔄 انتقالات" },
                        { key: "can_query_customers", label: "👤 جستجوی مشتریان" },
                      ].map(opt => (
                        <label key={opt.key} style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                          background: newTgUser[opt.key] ? "rgba(16,185,129,0.1)" : (dark ? "rgba(255,255,255,0.03)" : "#fff"),
                          border: `1px solid ${newTgUser[opt.key] ? "rgba(16,185,129,0.3)" : T.border}`,
                        }}>
                          <input
                            type="checkbox"
                            checked={!!newTgUser[opt.key]}
                            onChange={e => setNewTgUser({ ...newTgUser, [opt.key]: e.target.checked })}
                            style={{ accentColor: "#10b981" }}
                          />
                          <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{opt.label}</span>
                        </label>
                      ))}
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>یادداشت</label>
                      <input
                        type="text"
                        value={newTgUser.notes}
                        onChange={e => setNewTgUser({ ...newTgUser, notes: e.target.value })}
                        placeholder="یادداشت اختیاری..."
                        style={inputStyle}
                      />
                    </div>

                    <button
                      onClick={addTgUser}
                      style={{
                        padding: "10px 24px", borderRadius: 8, border: "none",
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                      }}
                    >
                      ✅ افزودن کاربر
                    </button>
                  </div>
                )}

                {/* لیست کاربران */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {tgUsers.length === 0 && (
                    <div style={{ textAlign: "center", padding: 30, color: T.subtext, fontSize: 13 }}>
                      هیچ کاربری ثبت نشده. روی "کاربر جدید" کلیک کنید.
                    </div>
                  )}
                  {tgUsers.map(u => {
                    const isEditing = editingTgUser?.id === u.id;
                    const cur = isEditing ? editingTgUser : u;
                    return (
                      <div key={u.id} style={{
                        padding: 14, borderRadius: 10,
                        background: dark ? "rgba(255,255,255,0.02)" : "#fafbfc",
                        border: `1px solid ${T.border}`,
                        opacity: u.is_active ? 1 : 0.6,
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                              <span style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{u.full_name}</span>
                              {u.is_active ? (
                                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "rgba(16,185,129,0.15)", color: "#10b981", fontWeight: 700 }}>فعال</span>
                              ) : (
                                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "rgba(239,68,68,0.15)", color: "#ef4444", fontWeight: 700 }}>غیرفعال</span>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: T.subtext }}>
                              <span>🆔 <code style={{ color: "#3b82f6", direction: "ltr" }}>{u.telegram_chat_id}</code></span>
                              {u.phone && <span>📞 <span style={{ direction: "ltr" }}>{u.phone}</span></span>}
                              {u.last_seen && <span>👀 آخرین فعالیت: {u.last_seen}</span>}
                            </div>
                          </div>
                          {isAdmin && (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                onClick={() => testTgSend(u.telegram_chat_id)}
                                title="ارسال پیام تست"
                                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #3b82f6", background: "transparent", color: "#3b82f6", cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                              >
                                📤 تست
                              </button>
                              <button
                                onClick={() => setEditingTgUser(isEditing ? null : { ...u, notify_sales: !!u.notify_sales, notify_purchases: !!u.notify_purchases, notify_cogs: !!u.notify_cogs, notify_payment_in: !!u.notify_payment_in, notify_payment_out: !!u.notify_payment_out, notify_expense: !!u.notify_expense, notify_transfers: !!u.notify_transfers, can_query_customers: !!u.can_query_customers, is_active: !!u.is_active })}
                                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #8b5cf6", background: "transparent", color: "#8b5cf6", cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                              >
                                {isEditing ? "✗ بستن" : "✏️ ویرایش"}
                              </button>
                              <button
                                onClick={() => deleteTgUser(u.id, u.full_name)}
                                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #ef4444", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                              >
                                🗑 حذف
                              </button>
                            </div>
                          )}
                        </div>

                        {isEditing && (
                          <div style={{ paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                              <div>
                                <label style={labelStyle}>نام کامل</label>
                                <input type="text" value={cur.full_name} onChange={e => setEditingTgUser({ ...cur, full_name: e.target.value })} style={inputStyle} />
                              </div>
                              <div>
                                <label style={labelStyle}>📞 تلفن</label>
                                <input type="text" value={cur.phone || ""} onChange={e => setEditingTgUser({ ...cur, phone: e.target.value })} style={inputStyle} />
                              </div>
                              <div>
                                <label style={labelStyle}>وضعیت</label>
                                <select value={cur.is_active ? "1" : "0"} onChange={e => setEditingTgUser({ ...cur, is_active: e.target.value === "1" })} style={inputStyle}>
                                  <option value="1">✅ فعال</option>
                                  <option value="0">❌ غیرفعال</option>
                                </select>
                              </div>
                            </div>

                            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 8 }}>
                              📊 گزارش‌ها:
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
                              {[
                                { key: "notify_sales", label: "🛒 فروشات" },
                                { key: "notify_purchases", label: "🛍️ خریدات" },
                                { key: "notify_cogs", label: "📦 COGS" },
                                { key: "notify_payment_in", label: "💰 دریافت‌ها" },
                                { key: "notify_payment_out", label: "💸 پرداخت‌ها" },
                                { key: "notify_expense", label: "🧾 مصارف" },
                                { key: "notify_transfers", label: "🔄 انتقالات" },
                                { key: "can_query_customers", label: "👤 جستجوی مشتریان" },
                              ].map(opt => (
                                <label key={opt.key} style={{
                                  display: "flex", alignItems: "center", gap: 6,
                                  padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                                  background: cur[opt.key] ? "rgba(139,92,246,0.1)" : (dark ? "rgba(255,255,255,0.03)" : "#fff"),
                                  border: `1px solid ${cur[opt.key] ? "rgba(139,92,246,0.3)" : T.border}`,
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={!!cur[opt.key]}
                                    onChange={e => setEditingTgUser({ ...cur, [opt.key]: e.target.checked })}
                                    style={{ accentColor: "#8b5cf6" }}
                                  />
                                  <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{opt.label}</span>
                                </label>
                              ))}
                            </div>

                            <button
                              onClick={() => updateTgUser(cur)}
                              style={{
                                padding: "8px 22px", borderRadius: 8, border: "none",
                                background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                                color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                              }}
                            >
                              💾 ذخیره تغییرات
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ─── بخش 3: راهنما ─── */}
              <div style={{ ...cardStyle, marginTop: 14 }}>
                {sectionTitle("راهنما", "📘")}
                <div style={{ fontSize: 12, color: T.subtext, lineHeight: 2 }}>
                  <p><strong>1. ساخت ربات:</strong> در تلگرام به <code style={{ color: "#3b82f6", direction: "ltr" }}>@BotFather</code> پیام بدهید، دستور <code>/newbot</code> را بزنید و توکن را کپی کنید.</p>
                  <p><strong>2. ذخیره توکن:</strong> توکن را در فیلد بالا وارد کنید و دکمه "ذخیره تنظیمات" را بزنید.</p>
                  <p><strong>3. تنظیم Webhook:</strong> پس از ذخیره توکن، دکمه "تنظیم Webhook" را بزنید.</p>
                  <p><strong>4. گرفتن شناسه چت:</strong> هر کاربر باید به ربات شما پیام دهد. بعد در سایت <code style={{ direction: "ltr", color: "#8b5cf6" }}>{`https://api.telegram.org/bot<TOKEN>/getUpdates`}</code> شناسه‌اش را پیدا کنید.</p>
                  <p><strong>5. افزودن کاربر:</strong> با شناسه چت، کاربر را در همین صفحه اضافه کنید.</p>
                  <p style={{ marginTop: 10, padding: "8px 14px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, color: "#f59e0b" }}>
                    ⚠️ گزارش خودکار روزانه نیاز به تنظیم <strong>Cron Job</strong> در پنل Hostinger دارد. این کار را با ادمین سرور هماهنگ کنید.
                  </p>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
// rebuild